// Pestana Caja y turnos: arqueos del periodo, desvio por cajero y mapa de
// ventas por dia y hora.
// Recibe todo ya calculado: cargar y agregar es cosa de la pantalla.
//
// Dos reglas que no se pueden aflojar acá:
//
// 1. CIERRE CIEGO. Con el flag activo y sin `caja-cierre-completo`, lo declarado
//    y la diferencia no se muestran — y ademas la pantalla ni siquiera los pide
//    a la DB (ver loadTurnos). Un reporte no puede ser la puerta trasera de un
//    permiso.
// 2. ARQUEOS INCOMPLETOS. `diferencia` suma todos los metodos de pago; si el
//    cajero declara solo el efectivo, el turno arrastra un negativo que no es
//    faltante. Nunca se totaliza el desvio de un arqueo incompleto — se lo
//    reporta aparte, como lo que es: un cierre sin terminar.

import React from 'react';
import { AlertTriangle, CalendarClock, Clock, Scale, Users, Wallet } from 'lucide-react';

import { formatInt, formatMoney } from './formato';
import { DataTable, EmptyState, KpiCard, SectionCard } from './componentes';
import { DIAS_SEMANA } from '../../utils/reportesCaja';

const fechaHora = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  // timeZone UTC: el POS y el cierre guardan hora forjada (local con sufijo Z).
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const signo = (n) => (n > 0 ? '+' : '');
const claseDesvio = (n) =>
  Math.abs(n) < 0.01 ? 'text-slate-500' : n < 0 ? 'text-rose-600' : 'text-emerald-600';

export default function TabCaja({
  arqueos,
  blindCashCloseEnabled,
  canViewFullClose,
  cargando,
  desvioPorCajero,
  periodLabel,
  resumenArqueos,
  ventasPorHora,
}) {
  if (cargando) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 rounded-2xl bg-white border border-slate-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!arqueos.length) {
    return (
      <SectionCard title="Caja y turnos" icon={Wallet}>
        <EmptyState icon={Wallet} message={`Sin turnos de caja en ${periodLabel}`} />
      </SectionCard>
    );
  }

  const { celdas, maxPromedio, horasActivas } = ventasPorHora;
  const celdaPorClave = new Map(celdas.map((c) => [`${c.dia}-${c.hora}`, c]));
  const diasConVentas = DIAS_SEMANA.map((_, i) => i).filter((d) =>
    celdas.some((c) => c.dia === d && c.tickets > 0),
  );

  return (
    <>
      <section>
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock size={14} className="text-slate-400" />
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">
            Turnos · {periodLabel}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={Wallet}
            label="Turnos cerrados"
            value={formatInt(resumenArqueos.cerrados)}
            accent="bg-blue-50 text-blue-600"
            hint={
              resumenArqueos.abiertos
                ? `${formatInt(resumenArqueos.abiertos)} sin cerrar`
                : 'Todos los turnos del periodo cerrados'
            }
          />
          <KpiCard
            icon={AlertTriangle}
            label="Arqueos incompletos"
            value={formatInt(resumenArqueos.arqueosIncompletos)}
            accent={
              resumenArqueos.arqueosIncompletos
                ? 'bg-amber-50 text-amber-600'
                : 'bg-emerald-50 text-emerald-600'
            }
            hint={
              resumenArqueos.metodosMasOmitidos.length
                ? `Sin declarar: ${resumenArqueos.metodosMasOmitidos
                    .slice(0, 2)
                    .map((m) => `${m.nombre} (${m.veces})`)
                    .join(' · ')}`
                : 'Todos los metodos declarados'
            }
          />
          {canViewFullClose && (
            <>
              <KpiCard
                icon={Scale}
                label="Desvio de caja"
                value={`${signo(resumenArqueos.desvioNeto)}$ ${formatMoney(resumenArqueos.desvioNeto)}`}
                accent={
                  Math.abs(resumenArqueos.desvioNeto) < 0.01
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-rose-50 text-rose-600'
                }
                hint={`Neto de ${formatInt(resumenArqueos.arqueosCompletos)} arqueos completos`}
              />
              <KpiCard
                icon={Scale}
                label="Desvio promedio"
                value={`$ ${formatMoney(resumenArqueos.desvioPromedio)}`}
                accent="bg-slate-100 text-slate-600"
                hint="Promedio absoluto por arqueo completo"
              />
            </>
          )}
        </div>
      </section>

      {/* El hallazgo, cuando existe: no es un faltante, es un cierre a medias. */}
      {resumenArqueos.arqueosIncompletos > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-[12px] text-amber-900 leading-relaxed">
              <p className="font-black uppercase tracking-widest text-[11px] mb-1">
                {formatInt(resumenArqueos.arqueosIncompletos)} de{' '}
                {formatInt(resumenArqueos.cerrados)} cierres quedaron a medias
              </p>
              <p>
                En esos turnos hubo metodos de pago con ventas que se declararon en cero al
                cerrar. La diferencia que arrastran <strong>no es un faltante de caja</strong>:
                es el teorico de lo que nunca se conto. Por eso quedan fuera del desvio del
                periodo
                {resumenArqueos.metodosMasOmitidos.length > 0 && (
                  <>
                    {' '}
                    — el que mas se omite es{' '}
                    <strong>{resumenArqueos.metodosMasOmitidos[0].nombre}</strong>
                  </>
                )}
                .
              </p>
            </div>
          </div>
        </div>
      )}

      <SectionCard
        title="Arqueos del periodo"
        icon={Wallet}
        action={
          blindCashCloseEnabled && !canViewFullClose ? (
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              cierre ciego
            </span>
          ) : null
        }
      >
        <DataTable
          emptyMessage="Sin turnos en el periodo"
          rows={arqueos}
          columns={[
            { key: 'apertura', label: 'Apertura', cellClass: () => 'text-slate-500', render: (a) => fechaHora(a.apertura) },
            { key: 'cierre', label: 'Cierre', cellClass: () => 'text-slate-500', render: (a) => (a.cerrado ? fechaHora(a.cierre) : '—') },
            { key: 'sucursal', label: 'Sucursal', cellClass: () => 'text-slate-600 truncate max-w-[140px]' },
            { key: 'usuario', label: 'Usuario', cellClass: () => 'font-bold text-slate-800 truncate max-w-[180px]' },
            { key: 'ventas', label: 'Ventas', align: 'right', cellClass: () => 'font-black text-slate-900', render: (a) => `$ ${formatMoney(a.ventas)}` },
            { key: 'tickets', label: 'Tickets', align: 'right', cellClass: () => 'text-slate-500', render: (a) => formatInt(a.tickets) },
            ...(canViewFullClose
              ? [
                  {
                    key: 'declarado',
                    label: 'Declarado',
                    align: 'right',
                    cellClass: () => 'text-slate-600',
                    render: (a) => (a.cerrado ? `$ ${formatMoney(a.declarado)}` : '—'),
                  },
                  {
                    key: 'diferencia',
                    label: 'Diferencia',
                    align: 'right',
                    cellClass: (a) => `font-black ${a.desvioConfiable ? claseDesvio(a.diferencia) : 'text-slate-400'}`,
                    // Una diferencia de arqueo incompleto no se muestra como
                    // numero: invita a leerla como faltante.
                    render: (a) =>
                      !a.cerrado ? '—' : a.desvioConfiable
                        ? `${signo(a.diferencia)}$ ${formatMoney(a.diferencia)}`
                        : 'sin arqueo',
                  },
                ]
              : []),
            {
              key: 'estado',
              label: 'Arqueo',
              align: 'right',
              render: (a) => {
                const [texto, clase, title] = !a.cerrado
                  ? ['Abierto', 'bg-blue-50 text-blue-700 border-blue-100', 'Turno todavia sin cerrar']
                  : a.sinResumen
                    ? ['Sin detalle', 'bg-slate-100 text-slate-600 border-slate-200', 'Cierre anterior a que se guardara el detalle por metodo']
                    : a.completo
                      ? ['Completo', 'bg-emerald-50 text-emerald-700 border-emerald-100', 'Se declararon todos los metodos con movimiento']
                      : ['Incompleto', 'bg-amber-50 text-amber-700 border-amber-100', `Sin declarar: ${a.metodosSinDeclarar.join(', ')}`];
                return (
                  <span
                    title={title}
                    className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border ${clase}`}
                  >
                    {texto}
                  </span>
                );
              },
            },
          ]}
        />
      </SectionCard>

      <SectionCard title="Por cajero" icon={Users}>
        <DataTable
          emptyMessage="Sin turnos asignados"
          rows={desvioPorCajero}
          columns={[
            { key: 'usuario', label: 'Usuario', cellClass: () => 'font-black text-slate-800 truncate max-w-[220px]' },
            { key: 'turnos', label: 'Turnos', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (c) => formatInt(c.turnos) },
            { key: 'ventas', label: 'Ventas', align: 'right', cellClass: () => 'font-black text-slate-900', render: (c) => `$ ${formatMoney(c.ventas)}` },
            { key: 'tickets', label: 'Tickets', align: 'right', cellClass: () => 'text-slate-500', render: (c) => formatInt(c.tickets) },
            {
              key: 'arqueosIncompletos',
              label: 'Incompletos',
              align: 'right',
              cellClass: (c) => `font-black ${c.arqueosIncompletos ? 'text-amber-600' : 'text-slate-400'}`,
              render: (c) => formatInt(c.arqueosIncompletos),
            },
            ...(canViewFullClose
              ? [
                  {
                    key: 'desvioNeto',
                    label: 'Desvio neto',
                    align: 'right',
                    cellClass: (c) => `font-black ${c.arqueosCompletos ? claseDesvio(c.desvioNeto) : 'text-slate-400'}`,
                    render: (c) =>
                      c.arqueosCompletos
                        ? `${signo(c.desvioNeto)}$ ${formatMoney(c.desvioNeto)}`
                        : '—',
                  },
                  {
                    key: 'desvioPromedio',
                    label: 'Prom. por arqueo',
                    align: 'right',
                    cellClass: () => 'text-slate-500',
                    render: (c) =>
                      c.arqueosCompletos ? `$ ${formatMoney(c.desvioPromedio)}` : '—',
                  },
                ]
              : []),
          ]}
        />
        <p className="mt-3 text-[10px] font-medium text-slate-400">
          Las ventas se atribuyen al turno que las contiene en el tiempo, no al usuario de la
          venta: ese campo no lo completan todas las instalaciones.
        </p>
      </SectionCard>

      <SectionCard
        title="Ventas por dia y hora"
        icon={Clock}
        action={
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            promedio por dia
          </span>
        }
      >
        {!horasActivas.length ? (
          <EmptyState icon={Clock} message="Sin ventas en el periodo" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate" style={{ borderSpacing: '2px' }}>
              <thead>
                <tr>
                  <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pr-2" />
                  {horasActivas.map((h) => (
                    <th
                      key={h}
                      className="text-[9px] font-black text-slate-400 tabular-nums"
                      style={{ minWidth: 26 }}
                    >
                      {String(h).padStart(2, '0')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diasConVentas.map((dia) => (
                  <tr key={dia}>
                    <td className="text-[10px] font-black uppercase tracking-widest text-slate-500 pr-2 whitespace-nowrap">
                      {DIAS_SEMANA[dia]}
                    </td>
                    {horasActivas.map((h) => {
                      const c = celdaPorClave.get(`${dia}-${h}`);
                      const v = c?.ventasPromedio || 0;
                      // Rampa de una sola tonalidad: la intensidad es la unica
                      // variable, asi se lee como magnitud y no como categoria.
                      const intensidad = maxPromedio > 0 ? v / maxPromedio : 0;
                      return (
                        <td
                          key={h}
                          title={
                            v > 0
                              ? `${DIAS_SEMANA[dia]} ${String(h).padStart(2, '0')}h · $ ${formatMoney(v)} promedio · ${formatInt(Math.round(c.ticketsPromedio))} tickets`
                              : `${DIAS_SEMANA[dia]} ${String(h).padStart(2, '0')}h · sin ventas`
                          }
                          className="rounded-md h-7"
                          style={{
                            backgroundColor:
                              intensidad > 0
                                ? `rgba(81, 167, 253, ${0.12 + intensidad * 0.88})`
                                : '#F1F5F9',
                          }}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-[10px] font-medium text-slate-400">
              Cada celda es el promedio de ventas de ese dia y hora, dividido por las veces que
              ese dia cae dentro del rango: un periodo de 30 dias tiene 4 lunes y 5 martes, y sin
              promediar el martes se veria mas fuerte solo por eso.
            </p>
          </div>
        )}
      </SectionCard>
    </>
  );
}
